import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Users, ShoppingCart, Edit, Eye, Copy, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import { getDifficultyLabel } from '../../utils/constants';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  ultimate_fitness: 'Ultimate Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

/**
 * One-click "Include with Premium" toggle. Optimistic update with rollback
 * on failure so admins get instant feedback and never see a stale UI.
 */
const PremiumAccessibleSwitch = ({ program }) => {
  const { api } = useAuth();
  const [checked, setChecked] = useState(!!program.premium_accessible);
  const [saving, setSaving] = useState(false);

  const handleChange = async (next) => {
    setSaving(true);
    setChecked(next);
    try {
      await api().put(`/admin/programs/${program.id}`, { premium_accessible: next });
      // Keep the source-of-truth in sync so the parent's stale prop doesn't
      // clobber our optimistic state on re-render.
      program.premium_accessible = next;
      toast.success(next ? 'Included with Premium' : 'Removed from Premium bundle');
    } catch (e) {
      setChecked(!next);
      toast.error(e.response?.data?.detail || 'Could not update program');
    }
    setSaving(false);
  };

  return (
    <label className="flex items-center gap-2 cursor-pointer group" title="Toggle whether Premium members get this program for free">
      <div className="flex items-center gap-1.5">
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
        ) : (
          <Sparkles className={`h-3.5 w-3.5 transition-colors ${checked ? 'text-primary' : 'text-muted-foreground group-hover:text-primary/70'}`} />
        )}
        <span className={`text-[10px] uppercase tracking-widest font-heading transition-colors ${checked ? 'text-primary' : 'text-muted-foreground group-hover:text-white'}`}>
          {checked ? 'With Premium' : 'Premium off'}
        </span>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={handleChange}
        disabled={saving}
        data-testid={`admin-premium-toggle-${program.id}`}
      />
    </label>
  );
};

export const AdminProgramsTab = ({ programs, onDuplicate, onDelete }) => {
  const navigate = useNavigate();

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.map((program) => (
          <div key={program.id} className="bg-card border border-border p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1">
                <h4 className="font-heading text-lg text-white uppercase tracking-wider truncate">{program.title}</h4>
                <p className="text-muted-foreground text-sm">{program.duration_weeks} weeks · {getDifficultyLabel(program.difficulty)}</p>
              </div>
              <Badge className="bg-primary/20 text-primary shrink-0">{goalLabels[program.goal]}</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
              <span><Users className="inline h-4 w-4 mr-1" />{program.enrollment_count || 0} enrolled</span>
              <span><ShoppingCart className="inline h-4 w-4 mr-1" />{program.purchase_count || 0} purchased</span>
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-3 mb-3">
              <PremiumAccessibleSwitch program={program} />
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate(`/admin/program/${program.id}`)}
                data-testid={`edit-program-${program.id}`}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => navigate(`/program/${program.id}`)}
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                onClick={() => onDuplicate(program.id, program.title)}
                title="Duplicate"
                data-testid={`duplicate-program-${program.id}`}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => onDelete(program.id)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};
